import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useFormState, useWatch, type UseFormReturn } from 'react-hook-form';
import { Section } from '@/shared/ui/layout/Section';
import { Select } from '@/shared/ui/selects/Select';
import { Button } from '@/shared/ui/buttons/Button';
import { useAddresses } from '@/entities/addresses';
import {
  applyRecipient,
  formatFullName,
  recipientMatchesValues,
  restoreScrollAfterApply,
  type RecipientField,
} from '@/shared/lib/recipient';
import type { ParcelFormValues } from '@/features/parcel-form/modules/types';

type Props = {
  form: UseFormReturn<ParcelFormValues>;
};

export const FeaturesParcelFormRecipientPicker = ({ form }: Props) => {
  const { t } = useTranslation('parcelForm');
  const navigate = useNavigate();
  const { data } = useAddresses();
  const addresses = useMemo(() => data ?? [], [data]);

  // Reactively read the form's current values so we can derive which saved
  // recipient (if any) the form currently equals. Fully derived: no local state,
  // no clear effect. Immune to remounts, re-renders, react-query refetches, and
  // RHF event-type quirks. Hooks must run before any early return. After a pick
  // fills the fields, values updates via useWatch, selectedId recomputes to that
  // address's id, and the Select shows it automatically; a user edit diverges a
  // field so nothing matches and the placeholder returns.
  const values = useWatch({ control: form.control });
  const { errors } = useFormState({ control: form.control });
  const selectedId = addresses.find((a) => recipientMatchesValues(a, values))?.id ?? '';

  // The recipient is now the sole source of the (hidden) address fields. When the
  // user submits without a recipient, zod flags the required address fields; since
  // that section is no longer rendered, surface a clear message here instead.
  const hasAddressError = Boolean(
    errors.address_name || errors.region || errors.city || errors.street || errors.building,
  );

  // No saved recipients: the form is otherwise unfillable, so guide the user to
  // create one instead of rendering an empty picker.
  if (addresses.length === 0) {
    return (
      <Section title={t('pickRecipient.title')}>
        <p className="text-sm text-ink-muted">{t('pickRecipient.emptyTitle')}</p>
        <div>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/settings/addresses/new')}
          >
            {t('pickRecipient.emptyAction')}
          </Button>
        </div>
      </Section>
    );
  }

  const options = addresses.map((a) => ({
    value: a.id,
    label: `${formatFullName(a, t('pickRecipient.noName'))}${a.label?.trim() ? ` — ${a.label}` : ''}`,
  }));

  const handleChange = (id: string): void => {
    if (!id) return;
    const found = addresses.find((a) => a.id === id);
    if (!found) return;
    const opts = { shouldDirty: true, shouldTouch: true } as const;
    const set = (field: RecipientField, value: string): void =>
      form.setValue(field, value as never, opts);
    restoreScrollAfterApply(() => applyRecipient(found, set));
  };

  return (
    <Section title={t('pickRecipient.title')}>
      <Select
        options={options}
        placeholder={t('pickRecipient.placeholder')}
        value={selectedId}
        onChange={(e) => handleChange(e.target.value)}
      />
      {hasAddressError && !selectedId ? (
        <p role="alert" className="text-xs text-red-600">
          {t('pickRecipient.required')}
        </p>
      ) : (
        <p className="text-xs text-ink-muted">{t('pickRecipient.autoFillHint')}</p>
      )}
    </Section>
  );
};
